import React, { useState, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Search,
  X,
  Check,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Briefcase,
  Users,
  Phone,
  ShoppingBag,
  ArrowRight,
  UserCheck,
  Calendar,
  Layers,
  FileText,
  Download,
  PlusCircle,
  ClipboardList,
  Package,
  UserPlus,
  Clock,
  ChevronRight,
  Bell
} from "lucide-react";
import { Line, Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, ArcElement, Filler } from 'chart.js';
import { useDashboard } from "../hooks/useDashboard";
import { formatCurrency } from "@/components/ui";
import { useAuthStore } from "@/store/authStore";
import { exportToPDF } from "../../../utils/exportUtils";
import FilterSection from "../../../components/ui/FilterSection";
import toast from "react-hot-toast";

// Register ChartJS modules globally inside the file for robustness
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ArcElement, Title, Tooltip, Legend, Filler);

// Predefined workflow levels
const WORKFLOW_LEVELS = [
  { id: "started", label: "Started", desc: "Project Initialized" },
  { id: "designed", label: "Designed", desc: "Design & Estimate Approved" },
  { id: "materials_ordered", label: "Materials Ordered", desc: "Factory Materials Placed" },
  { id: "installation", label: "Installation", desc: "On-site Installation" },
  { id: "completed", label: "Completed", desc: "Handover Complete" }
];

export const OwnerDashboard = () => {
  const { user: authUser } = useAuthStore();
  const [workflowSearch, setWorkflowSearch] = useState("");
  const [showWorkflowSearch, setShowWorkflowSearch] = useState(false);
  const [trackedProject, setTrackedProject] = useState(null);
  const [workflowError, setWorkflowError] = useState("");

  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ date: 'all', fromDate: '', toDate: '' });
  const dashboardRef = useRef(null);
  const [isExporting, setIsExporting] = useState(false);

  const {
    analytics,
    monthlySalesBarData,
    recentLeads,
    activeProjects,
    allLeads,
    allProjects,
    sales,
    received,
    expenses,
    orderRequests,
    inventoryMaster,
    purchaseReturns,
    saleReturns,
    openingStock,
    bookEntries,
    users,
    loading
  } = useDashboard(search, filters);

  // General Expenses specific filtering
  const { generalExpensesList, totalGeneralExpenses } = useMemo(() => {
    let filtered = (expenses || []).filter(e => e.category === 'general' || !e.relatedToProject);
    
    try {
      const storeRef = JSON.parse(localStorage.getItem('company_user') || '{}').storeRef;
      if (storeRef && storeRef !== 'all') {
        filtered = filtered.filter(e => e.storeRef?._id === storeRef || e.storeRef === storeRef);
      }
    } catch(err){}

    const sorted = [...filtered].sort((a, b) => new Date(b.date || b.createdAt) - new Date(a.date || a.createdAt));
    const total = filtered.reduce((sum, e) => sum + (Number(e.approvedAmount || e.amount) || 0), 0);
    
    return {
      generalExpensesList: sorted.slice(0, 5),
      totalGeneralExpenses: total
    };
  }, [expenses]);

  React.useEffect(() => {
    if (allLeads && allLeads.length > 0) {
      console.log("ALL_LEADS_DEBUG:", allLeads.slice(0, 5));
    }
  }, [allLeads]);

  const handleExport = async () => {
    if (loading || isExporting || !dashboardRef.current) return;
    try {
      setIsExporting(true);

      const pad = n => String(n).padStart(2, '0');
      const d = new Date();
      const dateStr = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

      await exportToPDF(dashboardRef.current, `Space_Designs_Dashboard_${dateStr}.pdf`);
      toast.success('Dashboard exported to PDF successfully');
    } catch (err) {
      console.error(err);
      toast.error('Failed to export dashboard PDF');
    } finally {
      setIsExporting(false);
    }
  };

  // 1. Project Workflow Status Mapper
  const getProjectWorkflowLevel = (status) => {
    const st = (status || "").toLowerCase().trim();
    if (["completed", "handover"].includes(st)) return 4; // Completed
    if (["installation_in_progress", "carpenter complete", "electrician complete", "painter complete", "plumber complete", "installation"].includes(st)) return 3; // Installation
    if (["materials_requested", "material requested", "pending store", "owner approval", "dispatched", "materials ordered", "materials"].includes(st)) return 2; // Materials Ordered
    if (["design", "design_approval", "cust_acceptance", "customer_acceptance", "designed"].includes(st)) return 1; // Designed
    return 0; // Started
  };

  // Handle Workflow search
  const handleWorkflowSearchSubmit = (e) => {
    e.preventDefault();
    if (!workflowSearch.trim()) {
      setWorkflowError("Please enter a valid Space Designs ID");
      setTrackedProject(null);
      return;
    }

    const query = workflowSearch.toLowerCase().trim();
    const match = allLeads.find(l => {
      const vid = (l.customId || l.projectId || l.projectNumber || l.leadId || l._id || "").toLowerCase();
      const name = (l.customerName || "").toLowerCase();
      return vid.includes(query) || name.includes(query);
    });

    if (match) {
      setTrackedProject(match);
      setWorkflowError("");
    } else {
      setTrackedProject(null);
      setWorkflowError(`No project found matching "${workflowSearch}"`);
    }
  };

  const clearWorkflowSearch = () => {
    setWorkflowSearch("");
    setTrackedProject(null);
    setWorkflowError("");
  };

  // KPI calculations based on live data with robust fallback
  const kpis = useMemo(() => {
    // 1. Today's Enquiries (Leads created today)
    const todayQuotations = allLeads.filter(l => {
      if (!l.createdAt) return false;
      return new Date(l.createdAt).toLocaleDateString('en-GB') === new Date().toLocaleDateString('en-GB');
    }).length;

    // 2. Open Quotations (Total Estimations Shared) -> Status is "Estimation Shared"
    const openQuotes = allLeads.filter(l => {
      const status = String(l.status || '').toLowerCase().trim();
      return status === 'estimation shared';
    }).length;

    // 3. Confirmed Sales Orders (Order Confirmed) -> Status is "Order Confirmed"
    const confirmedOrders = allLeads.filter(l => {
      const status = String(l.status || '').toLowerCase().trim();
      return status === 'order confirmed';
    }).length;

    // 4. Active Production Orders (Order Requests)
    const activeProds = (orderRequests || []).length;

    // 5. Pending Payments (Order Confirmed but not fully paid)
    let pendingPaymentsCount = 0;
    let pendingAmountTotal = 0;
    allLeads.forEach(l => {
       if (String(l.status).toLowerCase().trim() === 'order confirmed') {
          const custId = String(l._id);
          const rTotal = (received || []).filter(r => String(r.relatedProjectId?._id || r.relatedProjectId) === custId).reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
          const sTotal = (sales || []).filter(s => String(s.project?._id || s.project || s.projectId?._id || s.projectId) === custId).reduce((sum, s) => sum + (Number(s.netPayable) || 0), 0);
          
          const expected = Math.max(Number(l.projectValue) || 0, sTotal);
          if (expected > rTotal || expected === 0) {
             pendingPaymentsCount++;
             pendingAmountTotal += Math.max(0, expected - rTotal);
          }
       }
    });

    // 6. Completed Projects
    const completedProjects = allLeads.filter(l => {
      const status = String(l.status || '').toLowerCase().trim();
      return status === 'completed';
    }).length;

    return [
      { label: "Today's Enquiries", value: todayQuotations || 0, desc: "New leads created today", change: "", color: "text-[#A27327]" },
      { label: "Open Quotations", value: openQuotes || 0, desc: "Total estimations shared", change: "", color: "text-[#A27327]" },
      { label: "Confirmed Sales Orders", value: confirmedOrders || 0, desc: "Order confirmed stages", change: "", color: "text-[#A27327]" },
      { label: "Active Production Orders", value: activeProds || 0, desc: "Currently in factory queue", change: "On schedule", color: "text-[#A27327]" },
      { label: "Pending Payments", value: pendingPaymentsCount || 0, desc: "Active project milestones", change: `₹${pendingAmountTotal.toLocaleString()} due`, color: "text-[#C91D25]" },
      { label: "Completed Projects", value: completedProjects || 0, desc: "Successfully delivered", change: "100% fulfilled", color: "text-emerald-600" }
    ];
  }, [allLeads, orderRequests, sales, received]);

  // Financial summary compact state
  const financeMetrics = useMemo(() => {
    // 1. Total Revenue: Book Entries Total + Inventory Sales
    let totalBookEntryRev = 0;
    (bookEntries || []).forEach(entry => {
       let include = true;
       try {
         const storeRef = JSON.parse(localStorage.getItem('company_user') || '{}').storeRef;
         if (storeRef && storeRef !== 'all') {
           const cStore = entry.customer_id?.store?._id || entry.customer_id?.store || entry.store;
           if (cStore !== storeRef) include = false;
         }
       } catch(e){}
       
       if (include) {
         totalBookEntryRev += (Number(entry.grandTotal || entry.grand_total) || 0);
       }
    });

    let totalSales = 0;
    (sales || []).forEach(s => {
       totalSales += (Number(s.netPayable || s.netBill || s.totalAmount || s.amount) || 0);
    });

    const totalRevenue = totalBookEntryRev + totalSales;

    // 2. Total Received (Accounts -> Received Amount)
    const totalReceived = (received || []).reduce((sum, r) => sum + (Number(r.amount) || 0), 0);

    // 3. Payables -> Total General Expenses (using the already calculated totalGeneralExpenses from the other useMemo)
    const payables = totalGeneralExpenses;

    // 4. Outstanding Payments -> Remaining balance
    const outstanding = Math.max(0, totalRevenue - totalReceived);
    
    return [
      { label: "Total Revenue", value: totalRevenue || 0, desc: "Combined book entries & sales", color: "text-[#A27327]", bg: "bg-[#FAF8F5] border-slate-100" },
      { label: "Total Received", value: totalReceived || 0, desc: "Total payments collected", color: "text-slate-800", bg: "bg-[#FAF8F5] border-slate-100" },
      { label: "Total General Expenses", value: payables || 0, desc: "Total non-project expenses", color: "text-slate-800", bg: "bg-[#FAF8F5] border-slate-100" },
      { label: "Outstanding Payments", value: outstanding || 0, desc: "Remaining balance to be collected", color: "text-[#C91D25]", bg: "bg-rose-50/10 border-rose-100/10" }
    ];
  }, [bookEntries, sales, received, totalGeneralExpenses]);

  // Inventory metrics matching the mockup
  const inventoryMetrics = useMemo(() => {
    // 1. Total Stock
    const totalStock = (inventoryMaster || []).reduce((sum, item) => sum + (Number(item.availableQty) || Number(item.stock) || 0), 0);
    
    // 2. Total Sales
    let totalSalesQty = 0;
    (sales || []).forEach(s => {
       if (s.items && Array.isArray(s.items)) {
          s.items.forEach(i => totalSalesQty += (Number(i.quantity) || 0));
       }
    });

    // 3. Purchase Returns
    let prQty = 0;
    (purchaseReturns || []).forEach(pr => {
       if (pr.items && Array.isArray(pr.items)) {
          pr.items.forEach(i => prQty += (Number(i.quantity) || Number(i.returnedQty) || 0));
       } else {
          prQty += (Number(pr.quantity) || 0);
       }
    });

    // 4 & 5. Sale Returns & Damaged
    let srQty = 0;
    let damagedQty = 0;
    (saleReturns || []).forEach(sr => {
       if (sr.items && Array.isArray(sr.items)) {
          sr.items.forEach(i => {
             srQty += (Number(i.quantity) || Number(i.returnedQty) || 0);
             damagedQty += (Number(i.damagedQty) || 0);
          });
       } else {
          srQty += (Number(sr.quantity) || 0);
          damagedQty += (Number(sr.damagedQty) || 0);
       }
    });

    // 6. Open Stock
    let osQty = 0;
    (openingStock || []).forEach(os => {
       if (os.items && Array.isArray(os.items)) {
          os.items.forEach(i => osQty += (Number(i.quantity) || Number(i.receivedQty) || Number(i.orderedQty) || 0));
       }
    });

    // We'll normalize the max value for the progress bars. If everything is 0, maxVal is 1.
    const maxVal = Math.max(totalStock, totalSalesQty, prQty, srQty, damagedQty, osQty, 1);
    
    const getPct = (val) => {
       const pct = Math.round((val / maxVal) * 100);
       return pct > 100 ? 100 : pct;
    };

    return [
      { label: "Total Stock", value: totalStock, unit: "units", pct: getPct(totalStock), color: "bg-teal-500", textValueColor: "text-teal-700" },
      { label: "Total Sales", value: totalSalesQty, unit: "units", pct: getPct(totalSalesQty), color: "bg-[#3b82f6]", textValueColor: "text-[#3b82f6]" },
      { label: "Purchase Returns", value: prQty, unit: "qty", pct: getPct(prQty), color: "bg-amber-500", textValueColor: "text-amber-600" },
      { label: "Sale Returns", value: srQty, unit: "qty", pct: getPct(srQty), color: "bg-purple-500", textValueColor: "text-purple-600" },
      { label: "Damaged", value: damagedQty, unit: "qty", pct: getPct(damagedQty), color: "bg-red-500", textValueColor: "text-red-600" },
    ];
  }, [inventoryMaster, sales, purchaseReturns, saleReturns, openingStock]);

  // Delivery Schedule Timeline mock
  const deliveryTimeline = useMemo(() => {
    const activeDispatches = (sales || []).filter(s => ['dispatch_started', 'pending_store', 'received_store'].includes(s.dispatchStatus));
    return activeDispatches.map(s => ({
       time: new Date(s.dispatchStartedAt || s.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}),
       orderId: s.invoiceNo || 'N/A',
       destination: s.deliveryCity || 'N/A',
       item: s.items?.[0]?.itemName || 'Materials',
       status: s.dispatchStatus === 'dispatch_started' ? 'Dispatched' : 'Scheduled',
       color: s.dispatchStatus === 'dispatch_started' ? 'bg-amber-500' : 'bg-slate-300'
    })).slice(0, 5);
  }, [sales]);

  // Recent operational activity logs
  const activityLogs = useMemo(() => {
    return (recentLeads || []).slice(0, 4).map(l => ({
      time: new Date(l.createdAt).toLocaleDateString(),
      title: `New enquiry received from ${l.customerName || 'Customer'}`,
      category: "Sales pipeline",
      type: "info"
    }));
  }, [recentLeads]);

  // Recent Order Requests
  const recentFactoryOrders = useMemo(() => {
    let filteredRequests = orderRequests || [];
    
    // Filter by active store to match dashboard context
    try {
      const currentUser = JSON.parse(localStorage.getItem('company_user') || '{}');
      const storeRef = currentUser.storeRef;
      if (storeRef && storeRef !== 'all') {
        filteredRequests = filteredRequests.filter(r => 
          r.store?._id === storeRef || 
          r.store === storeRef || 
          r.customer_id?.store?._id === storeRef || 
          r.customer_id?.store === storeRef
        );
      }
    } catch (e) {
      console.error('Error filtering by store:', e);
    }

    return filteredRequests.slice(0, 10).map(r => {
      const creatorObj = r.requestedBy || r.estimation_id?.created_by || r.estimation_id?.requested_by || r.customer_id?.createdBy;
      const creatorName = creatorObj?.name || (creatorObj?.firstName ? `${creatorObj.firstName} ${creatorObj.lastName || ''}`.trim() : null) || (typeof r.requestedBy === 'string' ? r.requestedBy : "Unknown");
      const storeName = r.store?.name || r.storeName || r.customer_id?.store?.name || "No Store";
      
      const rawStatus = r.work_status || r.workStatus || r.status || "NOT_STARTED";
      let displayStatus = rawStatus.toUpperCase().replace(/_/g, " ");
      if (displayStatus === "RECEIVED IN STORE") displayStatus = "RECEIVED";
      
      let progress = 50;
      if (displayStatus === "NOT STARTED") progress = 0;
      if (displayStatus === "IN PROGRESS") progress = 50;
      if (displayStatus === "COMPLETED") progress = 100;
      if (displayStatus === "RECEIVED") progress = 100;

      return {
        date: r.createdAt ? new Date(r.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : "N/A",
        requestedBy: creatorName,
        customer: r.customer_id?.name || r.customer_id?.customerName || r.customerName || "—",
        sdId: r.customer_id?.sdId || r.sdId || "—",
        phone: r.customer_id?.phone || r.customer_id?.phoneNumber || r.phone || "—",
        store: storeName,
        status: displayStatus,
        progress: progress
      };
    });
  }, [orderRequests]);

  // 1. Monthly Book Entries Line Chart Data
  const salesVsTargetChartData = useMemo(() => {
    let storeRef = 'all';
    try {
      storeRef = JSON.parse(localStorage.getItem('company_user') || '{}').storeRef || 'all';
    } catch(e) {}

    const months = [];
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({
        label: d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }),
        month: d.getMonth(),
        year: d.getFullYear()
      });
    }

    const labels = months.map(m => m.label);

    if (storeRef !== 'all') {
      const data = new Array(6).fill(0);
      (bookEntries || []).forEach(entry => {
        const cStore = entry.customer_id?.store?._id || entry.customer_id?.store || entry.store;
        if (cStore === storeRef) {
          const date = new Date(entry.createdAt || entry.date || new Date());
          const monthIdx = months.findIndex(m => m.month === date.getMonth() && m.year === date.getFullYear());
          if (monthIdx !== -1) {
            data[monthIdx] += (Number(entry.grandTotal || entry.grand_total) || 0);
          }
        }
      });
      return {
        labels,
        datasets: [
          {
            label: "Book Entry (₹)",
            data,
            borderColor: "#A27327",
            backgroundColor: "rgba(162, 115, 39, 0.04)",
            borderWidth: 2.5,
            pointBackgroundColor: "#A27327",
            pointBorderColor: "#FFFFFF",
            pointHoverRadius: 6,
            tension: 0.35,
            fill: true
          }
        ]
      };
    } else {
      const storeDataMap = {};
      (bookEntries || []).forEach(entry => {
        const cStoreId = entry.customer_id?.store?._id || entry.customer_id?.store || entry.store || "unknown";
        const cStoreName = entry.customer_id?.store?.name || entry.customer_id?.store?.title || entry.storeName || entry.store?.name || entry.store?.title || "Unknown Store";
        
        if (!storeDataMap[cStoreId]) {
          storeDataMap[cStoreId] = {
            name: cStoreName,
            data: new Array(6).fill(0)
          };
        }
        
        const date = new Date(entry.createdAt || entry.date || new Date());
        const monthIdx = months.findIndex(m => m.month === date.getMonth() && m.year === date.getFullYear());
        if (monthIdx !== -1) {
          storeDataMap[cStoreId].data[monthIdx] += (Number(entry.grandTotal || entry.grand_total) || 0);
        }
      });

      const colors = ["#A27327", "#C91D25", "#2E2E2E", "#4B5563", "#10B981", "#3B82F6", "#8B5CF6"];
      let colorIdx = 0;

      const datasets = Object.values(storeDataMap).map(store => {
        const color = colors[colorIdx % colors.length];
        colorIdx++;
        return {
          label: store.name,
          data: store.data,
          borderColor: color,
          backgroundColor: "transparent",
          borderWidth: 2.5,
          pointBackgroundColor: color,
          pointBorderColor: "#FFFFFF",
          pointHoverRadius: 6,
          tension: 0.35,
          fill: false
        };
      });

      if (datasets.length === 0) {
        datasets.push({
          label: "Book Entry (₹)",
          data: new Array(6).fill(0),
          borderColor: "#A27327",
          backgroundColor: "rgba(162, 115, 39, 0.04)",
          borderWidth: 2.5,
          pointBackgroundColor: "#A27327",
          tension: 0.35,
          fill: true
        });
      }

      return { labels, datasets };
    }
  }, [bookEntries]);

  // 2. Lead Source Donut Chart Data
  const leadSourceChartData = useMemo(() => {
    let walkIn = 0, referral = 0, website = 0;
    (allLeads || []).forEach(l => {
       const s = String(l.leadSource || '').toLowerCase().trim();
       if (s === 'referral') referral++;
       else if (s === 'website') website++;
       else walkIn++;
    });
    const total = walkIn + referral + website;
    const placeholder = total === 0 ? 1 : 0;
    const w = total === 0 ? placeholder : walkIn;

    return {
      labels: ["Walk-in", "Referral", "Website"],
      total,
      walkInPct: total === 0 ? 0 : Math.round((walkIn/total)*100),
      referralPct: total === 0 ? 0 : Math.round((referral/total)*100),
      websitePct: total === 0 ? 0 : Math.round((website/total)*100),
      datasets: [{
        data: [w, referral, website],
        backgroundColor: [
          "#0f172a", // Walk-in (Navy)
          "#A27327", // Referral (Gold)
          "#cbd5e1"  // Website (Slate-300)
        ],
        hoverOffset: 6,
        borderWidth: 0
      }]
    };
  }, [allLeads]);

  return (
    <div
      ref={dashboardRef}
      className="flex flex-col gap-6 w-full max-w-full pb-8 overflow-x-hidden bg-[#FAF8F5] p-6 rounded-3xl"
      style={{ fontFamily: "'Inter', sans-serif" }}
    >
      <style>{`
        .custom-shadow {
          box-shadow: 0 4px 20px -2px rgba(162, 115, 39, 0.04), 0 2px 6px -1px rgba(0, 0, 0, 0.02);
        }
        .custom-card-hover {
          transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .custom-card-hover:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 30px -4px rgba(162, 115, 39, 0.08), 0 4px 12px -2px rgba(0, 0, 0, 0.03);
        }
        .font-serif-header {
          font-family: 'Playfair Display', serif;
        }
        @media print {
          .bg-white, .custom-shadow {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
        }
        .bg-white, .custom-shadow {
            page-break-inside: avoid;
            break-inside: avoid;
        }
      `}</style>

      {/* Page 1 Export Container */}
      <div className="flex flex-col gap-6 w-full">

        {/* 1. Header Banner */}
        <div className="relative overflow-hidden w-full h-44 bg-[#2E2E2E] rounded-[20px] flex items-center justify-between shadow-sm border border-[#E7E7E7]/25">
          <div className="absolute inset-0 bg-black/45 z-10" />
          <img
            src={require('../../../assets/Images/dashboard_banner.png')}
            alt="Space Designs Banner"
            className="absolute inset-0 w-full h-full object-cover object-center opacity-60"
          />

          <div className="pl-10 z-20 text-white flex flex-col gap-1.5">
            <span className="text-xs uppercase tracking-widest text-[#A27327] font-semibold">Strategic Operations Center</span>
            <h1 className="font-serif-header font-medium text-4xl text-white tracking-wide drop-shadow-sm">
              Operations Control Dashboard
            </h1>
            <p className="text-sm text-slate-200 font-light mt-1 font-body drop-shadow-sm italic">
              "Crafting Spaces That Inspire"
            </p>
          </div>
        </div>

        {/* Filter Section (Commented out per request, retaining Export Button) */}
        <div className="flex w-full mt-[-10px] pr-2 justify-end">
          <button
            onClick={handleExport}
            disabled={isExporting}
            className="relative flex items-center gap-2 bg-white text-slate-700 px-4 py-2.5 rounded-xl border border-[#E7E7E7] shadow-sm hover:bg-slate-50 transition-all duration-300 font-semibold text-sm cursor-pointer"
          >
            {isExporting ? (
              <div className="w-4 h-4 border-2 border-slate-300 border-t-[#A27327] rounded-full animate-spin" />
            ) : (
              <Download size={18} className="text-[#A27327]" />
            )}
            <span>{isExporting ? 'Exporting PDF...' : 'Export to PDF'}</span>
          </button>
        </div>

        {/* 2. Workflow Tracker Dashboard Banner */}
        {(trackedProject || workflowError) && (
          <div className="p-6 border-l-4 border-l-[#A27327] bg-white shadow-sm rounded-[20px] border border-[#E7E7E7] animate-in slide-in-from-top duration-300">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="font-semibold text-[#2E2E2E] text-lg font-serif-header">Project Workflow Tracker</h3>
                <p className="text-xs text-slate-400 mt-0.5">Real-time status tracking for target project ID</p>
              </div>
              <button
                onClick={clearWorkflowSearch}
                className="p-1 rounded-full text-slate-400 hover:bg-slate-50 hover:text-slate-600 transition-colors cursor-pointer"
              >
                <X size={18} />
              </button>
            </div>

            {workflowError ? (
              <div className="flex items-center gap-2 text-[#C91D25] bg-red-50/50 p-4 rounded-xl text-sm font-medium border border-red-100">
                <AlertCircle size={18} />
                <span>{workflowError}</span>
              </div>
            ) : (
              <div className="flex flex-col gap-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-[#FAF8F5] border border-[#E7E7E7]/50 rounded-xl text-sm">
                  <div>
                    <span className="text-slate-400 block text-xs">Space Designs ID / Project Name</span>
                    <span className="font-semibold text-slate-800 block mt-0.5">
                      {trackedProject.customId || trackedProject.projectId || trackedProject.projectNumber || trackedProject._id}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-400 block text-xs">Customer Name</span>
                    <span className="font-semibold text-slate-800 block mt-0.5">{trackedProject.customerName || trackedProject.name || "-"}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 block text-xs">Current Stage</span>
                    <span className="font-semibold text-[#A27327] uppercase block mt-0.5 text-xs bg-[#FAF8F5] border border-[#E7E7E7] w-fit px-2 py-0.5 rounded">
                      {trackedProject.leadStatus || trackedProject.status || "new"}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-400 block text-xs">Estimate Budget</span>
                    <span className="font-semibold text-slate-800 block mt-0.5">
                      {formatCurrency(trackedProject.estimatedBudget || trackedProject.projectValue || trackedProject.quotationAmount || trackedProject.budget || 0)}
                    </span>
                  </div>
                </div>

                <div className="relative mt-2">
                  <div className="absolute top-1/2 left-0 right-0 h-[2px] bg-slate-100 -translate-y-1/2 z-0 rounded-full" />
                  <div
                    className="absolute top-1/2 left-0 h-[2px] bg-[#A27327] -translate-y-1/2 z-0 rounded-full transition-all duration-500"
                    style={{ width: `${(getProjectWorkflowLevel(trackedProject.leadStatus || trackedProject.status) / (WORKFLOW_LEVELS.length - 1)) * 100}%` }}
                  />

                  <div className="relative z-10 flex justify-between">
                    {WORKFLOW_LEVELS.map((level, index) => {
                      const projectLevel = getProjectWorkflowLevel(trackedProject.leadStatus || trackedProject.status);
                      const isCompleted = index <= projectLevel;
                      const isActive = index === projectLevel;

                      return (
                        <div key={level.id} className="flex flex-col items-center text-center max-w-[120px]">
                          <div
                            className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${isCompleted
                                ? "bg-[#A27327] border-[#A27327] text-white shadow-md shadow-[#A27327]/10"
                                : "bg-white border-slate-200 text-slate-400"
                              } ${isActive ? "ring-4 ring-amber-50 border-[#A27327]" : ""}`}
                          >
                            {isCompleted ? <Check size={18} /> : <span className="text-sm font-semibold">{index + 1}</span>}
                          </div>
                          <span className={`text-xs font-semibold mt-2.5 ${isCompleted ? "text-slate-900" : "text-slate-400"}`}>
                            {level.label}
                          </span>
                          <span className="text-[10px] text-slate-400 mt-0.5 leading-tight font-medium hidden sm:block">
                            {level.desc}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* 3. KPI Operational Cards Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-5">
          {kpis.map((kpi, idx) => (
            <div
              key={idx}
              className="bg-white rounded-[20px] p-5 border border-[#E7E7E7] custom-shadow custom-card-hover flex flex-col justify-between"
            >
              <div className="flex flex-col gap-1">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 font-sans">{kpi.label}</span>
                <span className={`text-3xl font-bold font-serif-header ${kpi.color} mt-2`}>
                  {kpi.value}
                </span>
              </div>
              <div className="mt-4 pt-3 border-t border-slate-50">
                <span className="text-[10px] font-medium text-slate-500 leading-tight block">{kpi.desc}</span>
                <span className="text-[9px] font-bold text-[#A27327] mt-1 block uppercase tracking-wider">{kpi.change}</span>
              </div>
            </div>
          ))}
        </div>

        {/* 4. Quick Actions & Compact Finance Summary Row */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Quick Actions (5 cols) */}
          <div className="lg:col-span-5 bg-white rounded-[20px] p-6 border border-[#E7E7E7] custom-shadow flex flex-col justify-between">
            <div>
              <h3 className="font-serif-header text-lg font-medium text-[#2E2E2E]">Quick Actions</h3>
              <p className="text-xs text-slate-400 mt-0.5 mb-5">Access production and customer workflows directly</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Link to="/customers" className="flex items-center gap-3 p-3.5 rounded-xl border border-[#E7E7E7] hover:border-[#A27327] hover:bg-[#FAF8F5] transition-all duration-200 group text-left">
                <div className="p-2 rounded-lg bg-amber-50 text-[#A27327] group-hover:bg-[#A27327] group-hover:text-white transition-colors">
                  <UserPlus size={18} />
                </div>
                <div>
                  <span className="text-xs font-bold text-slate-700 block">Add Customer</span>
                  <span className="text-[9px] text-slate-400 block mt-0.5">Register client details</span>
                </div>
              </Link>

              <Link to="/notifications" className="flex items-center gap-3 p-3.5 rounded-xl border border-[#E7E7E7] hover:border-[#A27327] hover:bg-[#FAF8F5] transition-all duration-200 group text-left">
                <div className="p-2 rounded-lg bg-amber-50 text-[#A27327] group-hover:bg-[#A27327] group-hover:text-white transition-colors">
                  <Bell size={18} />
                </div>
                <div>
                  <span className="text-xs font-bold text-slate-700 block">Notifications</span>
                  <span className="text-[9px] text-slate-400 block mt-0.5">View latest updates</span>
                </div>
              </Link>

              <Link to="/teams" className="flex items-center gap-3 p-3.5 rounded-xl border border-[#E7E7E7] hover:border-[#A27327] hover:bg-[#FAF8F5] transition-all duration-200 group text-left">
                <div className="p-2 rounded-lg bg-amber-50 text-[#A27327] group-hover:bg-[#A27327] group-hover:text-white transition-colors">
                  <Users size={18} />
                </div>
                <div>
                  <span className="text-xs font-bold text-slate-700 block">Team</span>
                  <span className="text-[9px] text-slate-400 block mt-0.5">Manage staff & roles</span>
                </div>
              </Link>

              <Link to="/documents" className="flex items-center gap-3 p-3.5 rounded-xl border border-[#E7E7E7] hover:border-[#A27327] hover:bg-[#FAF8F5] transition-all duration-200 group text-left">
                <div className="p-2 rounded-lg bg-amber-50 text-[#A27327] group-hover:bg-[#A27327] group-hover:text-white transition-colors">
                  <FileText size={18} />
                </div>
                <div>
                  <span className="text-xs font-bold text-slate-700 block">Documents</span>
                  <span className="text-[9px] text-slate-400 block mt-0.5">Access files & folders</span>
                </div>
              </Link>
            </div>
          </div>

          {/* Compact Finance Summary (7 cols) */}
          <div className="lg:col-span-7 bg-white rounded-[20px] p-6 border border-[#E7E7E7] custom-shadow flex flex-col justify-between">
            <div className="flex justify-between items-center mb-5">
              <div>
                <h3 className="font-serif-header text-lg font-medium text-[#2E2E2E]">Finance Summary</h3>
                <p className="text-xs text-slate-400 mt-0.5">Compact operations ledger & cash positions</p>
              </div>
              <span className="text-[10px] font-bold text-[#A27327] bg-amber-50 border border-[#A27327]/10 px-2.5 py-1 rounded-full uppercase tracking-wider">
                Active base accounts
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 flex-1">
              {financeMetrics.map((metric, idx) => (
                <div
                  key={idx}
                  className={`rounded-xl p-4.5 border ${metric.bg} flex flex-col justify-center transition-all duration-200 hover:border-[#A27327]/30`}
                >
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{metric.label}</span>
                  <div className={`text-2xl font-bold font-serif-header mt-2 ${metric.color}`}>
                    {formatCurrency(metric.value)}
                  </div>
                  <p className="text-[10px] text-slate-400 mt-1 leading-normal font-medium">{metric.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 5. Primary Charts Section (Sales vs Target & Production Status) */}
        <div className="html2pdf__page-break"></div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Sales vs Target (8 cols) */}
          <div className="lg:col-span-8 bg-white rounded-[20px] p-6 border border-[#E7E7E7] custom-shadow">
            <div className="flex justify-between items-start mb-5">
              <div>
                <h3 className="font-serif-header text-lg font-medium text-[#2E2E2E]">Monthly Book Entries</h3>
                <p className="text-sm text-gray-500">Tracking comprehensive design bookings against project goals</p>
              </div>
              <div className="flex gap-4 text-xs font-semibold">
              </div>
            </div>
            <div className="h-[280px] relative">
              <Line
                data={salesVsTargetChartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { 
                    legend: { 
                      display: true, 
                      position: 'top',
                      align: 'end',
                      labels: { usePointStyle: true, boxWidth: 6, font: { family: "'Inter', sans-serif", size: 11 } }
                    } 
                  },
                  scales: {
                    y: {
                      ticks: { callback: (value) => '₹' + (value / 100000) + 'L' },
                      grid: { color: 'rgba(0, 0, 0, 0.03)' }
                    },
                    x: { grid: { display: false } }
                  }
                }}
              />
            </div>
          </div>

          {/* Lead Source Donut (4 cols) */}
          <div className="lg:col-span-4 bg-white rounded-[20px] p-6 border border-[#E7E7E7] custom-shadow flex flex-col justify-between">
            <div>
              <h3 className="font-serif-header text-lg font-medium text-[#2E2E2E]">Lead Source Contribution</h3>
              <p className="text-xs text-slate-400 mt-0.5">Share percentage of different lead pathways</p>
            </div>

            <div className="h-44 relative flex justify-center items-center my-4">
              <Doughnut
                data={leadSourceChartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { display: false } },
                  cutout: '72%'
                }}
              />
              <div className="absolute flex flex-col items-center">
                <span className="text-2.5xl font-bold text-[#2E2E2E] font-serif-header">100%</span>
                <span className="text-[9px] uppercase text-slate-400 font-bold tracking-widest mt-0.5">Lead Ratio</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs font-semibold text-slate-600 border-t border-slate-50 pt-4">
              <div className="flex justify-between items-center">
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#0f172a]" /> Walk-in</span>
                <span className="text-[#0f172a]">{leadSourceChartData.walkInPct}%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#A27327]" /> Referral</span>
                <span className="text-[#A27327]">{leadSourceChartData.referralPct}%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#cbd5e1]" /> Website</span>
                <span className="text-slate-500">{leadSourceChartData.websitePct}%</span>
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* Page 2 Export Container */}
      <div className="flex flex-col gap-6 w-full mt-4">

        <div className="html2pdf__page-break"></div>
        {/* 6. General Expenses & Inventory Health */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-6">
          
          {/* General Expenses (7 cols) */}
          <div className="lg:col-span-7 bg-white rounded-[20px] p-6 border border-[#E7E7E7] custom-shadow flex flex-col justify-between">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h3 className="font-serif-header text-2xl font-bold text-[#1e293b]">General Expenses (Non-Project)</h3>
                <p className="text-[13px] text-slate-400 mt-1">Office rental,Transport, Food Related Expenses, and marketing</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Total General Expenses</p>
                <p className="font-serif-header text-2xl font-bold text-[#1e293b]">
                  {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(totalGeneralExpenses)}
                </p>
              </div>
            </div>

            <div className="flex flex-col flex-grow w-full border border-slate-50 rounded-xl overflow-hidden shadow-sm">
              <table className="w-full text-left border-collapse text-xs font-sans">
                <tbody className="divide-y divide-slate-50 font-semibold text-slate-700 bg-white">
                  {generalExpensesList.map((expense, idx) => (
                    <tr key={idx} className="hover:bg-[#FAF8F5]/30 transition-colors">
                      <td className="py-4 px-5 text-slate-700 font-medium bg-slate-50/30 w-1/4">{expense.byWhomPaid || 'Unknown'}</td>
                      <td className="py-4 px-5 text-slate-500 font-medium">{expense.reason || '—'}</td>
                      <td className="py-4 px-5 text-slate-400 font-medium font-body whitespace-nowrap">{expense.date ? new Date(expense.date).toLocaleDateString('en-GB') : ''}</td>
                      <td className="py-4 px-5 text-slate-800 font-semibold whitespace-nowrap">
                        {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(expense.amount)}
                      </td>
                      <td className="py-4 px-5 text-right pr-6">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[9px] font-bold border uppercase tracking-wider ${
                          (expense.status || '').toLowerCase() === 'approved' 
                            ? "text-emerald-600 bg-emerald-50 border-emerald-100" 
                            : (expense.status || '').toLowerCase() === 'pending_approval' 
                              ? "text-amber-600 bg-amber-50 border-amber-100" 
                              : "text-slate-500 bg-slate-50 border-slate-200"
                        }`}>
                          {(expense.status || '').replace(/_/g, ' ')}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {generalExpensesList.length === 0 && (
                    <tr>
                      <td colSpan="5" className="py-8 text-center text-slate-400 font-medium">No recent general expenses found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Inventory Health Progress Bars (5 cols) */}
          <div className="lg:col-span-5 bg-white rounded-[20px] p-6 border border-[#E7E7E7] custom-shadow flex flex-col justify-between">
            <div>
              <h3 className="font-serif-header text-xl font-bold text-[#1e293b]">Inventory</h3>
              <p className="text-[13px] text-slate-400 mt-1">Stock levels, sales, returns, and damages</p>
            </div>

            <div className="flex flex-col gap-5 mt-6 pl-2 pr-6 pb-4">
              {inventoryMetrics.map((item, idx) => (
                <div key={idx} className="flex items-center gap-4">
                  {/* Left Label */}
                  <div className="w-28 text-right">
                    <span className="text-[13px] font-semibold text-slate-500">{item.label}</span>
                  </div>
                  
                  {/* Progress Bar Container */}
                  <div className="flex-1 h-[20px] bg-slate-100 rounded-full overflow-hidden flex items-center">
                    <div 
                      className={`h-full ${item.color} rounded-full`} 
                      style={{ width: `${Math.max(item.pct, 3)}%` }} 
                    />
                  </div>
                  
                  {/* Right Value & Unit */}
                  <div className="w-20 pl-2 flex items-baseline gap-1.5">
                    <span className={`text-[15px] font-bold ${item.textValueColor}`}>{item.value}</span>
                    <span className="text-[11px] font-semibold text-slate-400">{item.unit}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 7. Recent Order Requests Table */}
        <div className="bg-white rounded-[20px] p-6 border border-[#E7E7E7] custom-shadow flex flex-col">
          <div className="flex justify-between items-center mb-5">
            <div>
              <h3 className="font-serif-header text-lg font-medium text-[#2E2E2E]">Recent Order Requests</h3>
              <p className="text-xs text-slate-400 mt-0.5">Latest material and factory requests tracking</p>
            </div>
          </div>

          <div className="overflow-x-auto min-w-0 w-full">
            <table className="w-full text-left border-collapse text-xs font-sans">
              <thead>
                <tr className="border-b border-[#E7E7E7]/80 text-[10px] font-bold text-slate-400 uppercase tracking-wider bg-[#FAF8F5]">
                  <th className="py-3 px-4 font-bold rounded-l-xl">Ordered Date</th>
                  <th className="py-3 px-4 font-bold">Requested By</th>
                  <th className="py-3 px-4 font-bold">Customer Name</th>
                  <th className="py-3 px-4 font-bold">SD ID</th>
                  <th className="py-3 px-4 font-bold">Phone Number</th>
                  <th className="py-3 px-4 font-bold">Store</th>
                  <th className="py-3 px-4 font-bold">Status</th>
                  <th className="py-3 px-4 font-bold text-right rounded-r-xl">Progress</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50 font-semibold text-slate-700">
                {recentFactoryOrders.map((order, idx) => {
                  const statusColors = {
                    "COMPLETED": "text-teal-700 bg-teal-50 border-teal-100",
                    "IN PROGRESS": "text-[#A27327] bg-amber-50/50 border-[#A27327]/10",
                    "NOT STARTED": "text-slate-600 bg-slate-50 border-slate-100",
                    "RECEIVED": "text-blue-700 bg-blue-50 border-blue-100"
                  };
                  
                  const activeColor = statusColors[order.status] || statusColors["NOT STARTED"];

                  return (
                    <tr key={idx} className="hover:bg-[#FAF8F5]/50 transition-colors">
                      <td className="py-3.5 px-4 text-slate-500 font-medium font-body whitespace-nowrap">{order.date}</td>
                      <td className="py-3.5 px-4 text-[#2E2E2E]">{order.requestedBy}</td>
                      <td className="py-3.5 px-4 text-[#2E2E2E] font-medium">{order.customer}</td>
                      <td className="py-3.5 px-4 text-[#A27327] font-bold whitespace-nowrap">{order.sdId}</td>
                      <td className="py-3.5 px-4 text-slate-600">{order.phone}</td>
                      <td className="py-3.5 px-4 text-slate-500 whitespace-nowrap">{order.store}</td>
                      <td className="py-3.5 px-4">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[9px] font-bold border ${activeColor}`}>
                          {order.status}
                        </span>
                      </td>
                      <td className="py-3.5 px-4 flex justify-end">
                        <div className="flex items-center gap-3">
                          <div className="h-1.5 w-16 bg-slate-100 rounded-full overflow-hidden">
                            <div className={`h-full ${order.progress === 100 ? 'bg-teal-500' : 'bg-[#A27327]'} rounded-full`} style={{ width: `${order.progress}%` }} />
                          </div>
                          <span className="text-[10px] text-slate-400 font-medium font-body w-6 text-right">{order.progress}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

      </div>

    </div>
  );
};





